/**
 * entityRecordActions
 *
 * Imperative writers for the entity-record store. Used by `useEntityRecordStore`
 * and direct callers (page actions, scope-opener providers).
 *
 * Writers route through `persistDocument` for the live record subscription and
 * push optimistic cache updates to the `entityRecordsRegistry` singleton,
 * preserving dropdown / list-view freshness without React Context.
 *
 * Navigation delegates to a `NavigateFunction` reference installed by
 * `useEntityRecordStoreUrlSync`.
 */
import { addDoc, collection, deleteDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import type { NavigateFunction } from 'react-router-dom';
import { firestore } from '@/services/startFirestore';
import { DEFAULT_NEW_RECORD_NAME, RecordStatus } from '#types/System.Parameters.types';
import { AgentType } from '#types/System.types';
import type { DocId, EntityReferences, EntityRecord, Meta } from '#types/System.types';
import {
    addRecordToCache,
    removeRecordFromCache,
    updateRecordInCache,
} from '@/store/entityRecordsRegistry/actions';
import {
    getEntityRecordPersist,
    getEntityRecordStoreEntry,
    markRecordUnlockedOnMount,
} from './entityRecordStore';

let navigate: NavigateFunction | null = null;

const setNavigate = (next: NavigateFunction | null): void => {
    navigate = next;
};

const ensureNavigate = (): NavigateFunction => {
    if (!navigate) {
        throw new Error(
            '[entityRecordActions] navigate is not installed — ensure useEntityRecordStoreUrlSync() is called at the route root',
        );
    }
    return navigate;
};

const navigateToEntities = (): void => {
    ensureNavigate()('/entities');
};

const navigateToEntity = (entityId: string): void => {
    ensureNavigate()(`/entities/${entityId}`);
};

const navigateToEntityRecord = (entityId: string, recordId: string): void => {
    ensureNavigate()(`/entities/${entityId}/records/${recordId}`);
};

const setEntityRecord = async (
    entityId: string,
    recordId: string,
    updates: Partial<EntityRecord>,
    options?: { merge?: boolean; debounce?: boolean },
): Promise<void> => {
    const persist = getEntityRecordPersist(entityId, recordId);
    if (!persist) return;

    const debounce = options?.debounce ?? false;
    if (options?.merge ?? true) {
        await persist.setMerge(updates, { debounce });
    } else {
        await persist.update(updates as Parameters<typeof persist.update>[0], { debounce });
    }

    const entry = getEntityRecordStoreEntry(entityId, recordId);
    const current = entry?.state.entityRecord.data ?? null;
    const merged = current ? { ...current, ...updates } : (updates as EntityRecord);
    const metadata: EntityRecord = {
        docId: recordId,
        name: merged.name || DEFAULT_NEW_RECORD_NAME,
        entityGroupIds: merged.entityGroupIds,
        meta: {
            searchNameLowercase: (merged.name || DEFAULT_NEW_RECORD_NAME).toLowerCase(),
            lastModified: Timestamp.now(),
        },
    };
    updateRecordInCache(entityId, metadata);
};

const debouncedSetEntityRecord = (
    entityId: string,
    recordId: string,
    updates: Partial<EntityRecord>,
): void => {
    void setEntityRecord(entityId, recordId, updates, { debounce: true });
};

/**
 * Creates a record, optionally already pointing at another record.
 *
 * `reference` lands in the new record's `entityReferences` under the referenced
 * record's `entityGroupId || entityId`, the key convention every reader resolves.
 * It is written here rather than in a follow-up merge because the `docMirror`
 * trigger derives the reverse index from the created document: a reference added
 * afterwards costs a second trigger pass and leaves the target listing the new
 * record as absent in between.
 */
const createEntityRecord = async (
    entityId: string,
    userId: string | undefined,
    entityGroupId?: string,
    name?: string,
    reference?: {
        entityId: string;
        recordId: DocId;
        recordName?: string;
        entityGroupId?: string;
    },
): Promise<DocId> => {
    const recordName = name?.trim() || DEFAULT_NEW_RECORD_NAME;
    const meta: Meta = {
        userId,
        created: serverTimestamp(),
        lastModified: serverTimestamp(),
        searchNameLowercase: recordName.toLowerCase(),
    };
    const entityReferences: EntityReferences = reference
        ? {
              [reference.entityGroupId || reference.entityId]: {
                  entityRecordId: reference.recordId,
                  entityId: reference.entityId,
                  entityRecordName: reference.recordName ?? '',
                  ...(reference.entityGroupId ? { entityGroupId: reference.entityGroupId } : {}),
                  meta: {
                      created: serverTimestamp(),
                      lastModified: serverTimestamp(),
                      userId,
                      agentType: AgentType.User,
                  },
              },
          }
        : {};
    const newRecord = {
        name: recordName,
        fields: {},
        entityReferences,
        ...(entityGroupId ? { entityGroupIds: [entityGroupId] } : {}),
        status: RecordStatus.Active,
        meta,
    };

    const ref = await addDoc(collection(firestore, `entities/${entityId}/records`), newRecord);
    markRecordUnlockedOnMount(ref.id);

    const metadata: EntityRecord = {
        docId: ref.id,
        name: recordName,
        entityGroupIds: entityGroupId ? [entityGroupId] : undefined,
        meta: {
            searchNameLowercase: recordName.toLowerCase(),
            lastModified: Timestamp.now(),
        },
    };
    addRecordToCache(entityId, metadata, entityGroupId);

    return ref.id;
};

const deleteEntityRecord = async (entityId: string, recordId: DocId): Promise<void> => {
    await deleteDoc(doc(firestore, `entities/${entityId}/records/${recordId}`));
    removeRecordFromCache(entityId, recordId);
};

const setEntityLocked = (entityId: string, recordId: string | undefined, value: boolean): void => {
    const entry = getEntityRecordStoreEntry(entityId, recordId);
    if (entry) entry.state.entityLocked = value;
};

const selectEntityGroup = (entityId: string, recordId: string | undefined, groupId: string | undefined): void => {
    const entry = getEntityRecordStoreEntry(entityId, recordId);
    if (entry) entry.state.entityGroupId = groupId;
};

export const entityRecordActions = {
    navigate: { setNavigate },
    navigateToEntities,
    navigateToEntity,
    navigateToEntityRecord,
    setEntityRecord,
    debouncedSetEntityRecord,
    createEntityRecord,
    deleteEntityRecord,
    setEntityLocked,
    selectEntityGroup,
};
